import React, { useEffect, useState } from 'react'
import { Envelope, PersonCircle, Plus, X } from 'react-bootstrap-icons'
import axiosService from '../../../../../../utils/AxiosService'
import { toastError } from '../../../../../FormComponents/Toasts'
import Modal from '../../../../../Modal/Modal'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function SendToClientModal({ isOpen, onClose, baseUrl, onSent }) {
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [client, setClient] = useState(null)
  const [contacts, setContacts] = useState([])
  const [previousRecipients, setPreviousRecipients] = useState([])
  const [selectedContactIds, setSelectedContactIds] = useState([])
  const [extraEmails, setExtraEmails] = useState([])
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState(null)
  const [saveNewEmails, setSaveNewEmails] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    setSelectedContactIds([])
    setExtraEmails([])
    setEmailInput('')
    setEmailError(null)
    setSaveNewEmails(false)
    axiosService.get(`${baseUrl}/client_recipients`)
      .then(data => {
        setClient(data.client)
        setContacts(data.contacts)
        setPreviousRecipients(data.previous_recipients)
      })
      .catch(response => {
        toastError(response?.data?.error || 'Could not load client contacts')
        onClose()
      })
      .finally(() => setLoading(false))
  }, [isOpen])

  const normalized = email => email.trim().toLowerCase()
  const contactEmails = contacts.filter(c => c.email).map(c => normalized(c.email))

  const selectedEmails = [
    ...contacts.filter(c => selectedContactIds.includes(c.id)).map(c => normalized(c.email)),
    ...extraEmails,
  ]

  const toggleContact = id => {
    setSelectedContactIds(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id])
  }

  const addEmail = (raw = emailInput) => {
    const email = normalized(raw)
    if (!email) return true
    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Enter a valid email address')
      return false
    }

    const contact = contacts.find(c => c.email && normalized(c.email) === email)
    if (contact) {
      setSelectedContactIds(ids => ids.includes(contact.id) ? ids : [...ids, contact.id])
    } else if (!extraEmails.includes(email)) {
      setExtraEmails(emails => [...emails, email])
    }
    setEmailInput('')
    setEmailError(null)
    return true
  }

  const usePreviousRecipients = () => {
    const matchedIds = contacts.filter(c => c.email && previousRecipients.map(normalized).includes(normalized(c.email))).map(c => c.id)
    const others = previousRecipients.map(normalized).filter(email => !contactEmails.includes(email))
    setSelectedContactIds(ids => [...new Set([...ids, ...matchedIds])])
    setExtraEmails(emails => [...new Set([...emails, ...others])])
  }

  const pendingInputEmail = EMAIL_REGEX.test(normalized(emailInput)) ? normalized(emailInput) : null
  const recipientCount = new Set([...selectedEmails, ...(pendingInputEmail ? [pendingInputEmail] : [])]).size

  const send = () => {
    if (emailInput.trim() && !addEmail()) return

    const emails = [...new Set([...extraEmails, ...(pendingInputEmail && !contactEmails.includes(pendingInputEmail) ? [pendingInputEmail] : [])])]
    const pendingContact = pendingInputEmail && contacts.find(c => c.email && normalized(c.email) === pendingInputEmail)
    const contactIds = [...new Set([...selectedContactIds, ...(pendingContact ? [pendingContact.id] : [])])]

    if (contactIds.length === 0 && emails.length === 0) return

    setSending(true)
    axiosService.post(`${baseUrl}/send_to_client`, { contact_ids: contactIds, emails, save_new_emails: saveNewEmails })
      .then(version => onSent(version))
      .catch(response => toastError(response?.data?.error || 'Could not send to client'))
      .finally(() => setSending(false))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>Send to client</Modal.Header>
      <Modal.Body>
        <div className="w-[560px] max-w-full flex flex-col gap-5">
          <p className="text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey">
            Choose who should receive the review link{client?.name ? <> for <span className="font-semibold text-rb-black dark:text-white">{client.name}</span></> : null}. Only the recipients you select will get an email.
          </p>

          {loading ? (
            <p className="text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey">Loading contacts…</p>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-sm dark:text-white">Client contacts</p>
                  {previousRecipients.length > 0 && (
                    <button type="button" onClick={usePreviousRecipients} className="text-xs font-semibold text-rb-green hover:underline">
                      Use last recipients
                    </button>
                  )}
                </div>

                {contacts.length === 0 ? (
                  <p className="text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey border-1 border-dashed border-rb-lite-grey dark:border-rb-dark-mode-lighter rounded-lg p-3">
                    This client has no saved contacts yet. Add an email address below.
                  </p>
                ) : (
                  <div className="flex flex-col border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg divide-y divide-rb-widget-border dark:divide-rb-dark-mode-lighter max-h-60 overflow-y-auto">
                    {contacts.map(contact => {
                      const disabled = !contact.email
                      const checked = selectedContactIds.includes(contact.id)

                      return (
                        <label
                          key={contact.id}
                          className={`flex items-center gap-3 px-3 py-2.5 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-rb-dark-mode-lighter/50'}`}
                        >
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={checked}
                            onChange={() => toggleContact(contact.id)}
                            className="rounded text-rb-green focus:ring-rb-green"
                          />
                          <PersonCircle className="w-6 h-6 shrink-0 text-rb-mat-grey dark:text-rb-dark-mode-text-grey" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate dark:text-white">{contact.name || contact.email || 'Unnamed contact'}</p>
                            {(contact.name || !contact.email) && <p className="text-xs truncate text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{contact.email || 'No email address'}</p>}
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>

              <div>
                <p className="font-bold text-sm mb-2 dark:text-white">Other email addresses</p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-rb-mat-grey" />
                    <input
                      type="email"
                      value={emailInput}
                      onChange={e => { setEmailInput(e.target.value); setEmailError(null) }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault()
                          addEmail()
                        }
                      }}
                      placeholder="name@company.com"
                      className="w-full pl-9 p-2 text-sm border-1 border-rb-lite-grey rounded-lg focus:ring-transparent focus:border-rb-green dark:bg-rb-dark-mode dark:text-white dark:border-rb-dark-mode-lighter"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => addEmail()}
                    disabled={!emailInput.trim()}
                    className="flex items-center gap-1 px-3 text-sm font-semibold rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}

                {extraEmails.length > 0 && (
                  <>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {extraEmails.map(email => (
                        <span key={email} className="flex items-center gap-1 bg-rb-green/10 text-rb-black dark:text-white text-xs font-semibold pl-3 pr-1 py-1 rounded-full">
                          {email}
                          <button type="button" onClick={() => setExtraEmails(emails => emails.filter(e => e !== email))} className="p-0.5 rounded-full hover:bg-rb-green/20" aria-label={`Remove ${email}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer dark:text-white">
                      <input
                        type="checkbox"
                        checked={saveNewEmails}
                        onChange={e => setSaveNewEmails(e.target.checked)}
                        className="rounded text-rb-green focus:ring-rb-green"
                      />
                      Also save {extraEmails.length === 1 ? 'this address' : 'these addresses'} to {client?.name || 'the client'}'s contacts
                    </label>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex justify-between items-center">
          <Modal.DissmissButton className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 text-rb-black hover:bg-gray-200 dark:bg-rb-dark-mode-lighter dark:text-white dark:hover:bg-rb-dark-mode-lighter/70">
            Cancel
          </Modal.DissmissButton>
          <button
            type="button"
            onClick={send}
            disabled={loading || sending || recipientCount === 0}
            className="px-4 py-2 text-sm font-semibold text-white bg-rb-green rounded-lg dark:text-rb-dark-mode disabled:opacity-50"
          >
            {recipientCount === 0 ? 'Select recipients' : `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
